import { Link } from 'react-router-dom';
import { cn } from '@mlm/ui/lib/cn';
import { Block, CoachButton, EmptyCircle, Eyebrow, Row, SageCheck, Stat } from '../primitives';
import { AlignmentMeters } from '../alignment-meters';
import {
  coaching,
  type Action,
  type Milestone,
  type ProgramSummary,
  type Today,
} from '../../lib/api';
import { actionMeta, minutesLabel } from '../../lib/format';
import { useToggleAction } from '../../lib/queries';
import { useAffirmation } from '../../lib/use-affirmation';

/** An outline button rendered as a router link. */
export const linkButton =
  'inline-flex min-h-11 items-center justify-center border border-[rgba(16,32,26,0.22)] px-5 font-mono text-[0.68rem] uppercase tracking-[0.14em] text-ink hover:border-[rgba(16,32,26,0.5)]';

const REFLECTIVE = 'Guidance scores are reflective indices, not predictions.';

/** Today's #1 action: the screen's one gold decision. */
export function PrimaryAction({ today, size }: { today: Today; size?: 'lg' }) {
  const toggle = useToggleAction();
  const primary = today.actions[0];
  if (!primary) {
    return (
      <Block
        eyebrow="Your #1 action today"
        title="You're clear for today."
        size={size}
        action={
          <Link to="/achieve/actions" className={linkButton}>
            Add an action
          </Link>
        }
      >
        Enjoy the progress you've already made, or add one meaningful action.
      </Block>
    );
  }
  return (
    <Block
      eyebrow="Your #1 action today"
      title={primary.title}
      size={size}
      action={
        primary.status === 'done' ? (
          <p className="flex items-center gap-2 text-sm text-sage">
            <SageCheck size={18} /> Done. One step closer.
          </p>
        ) : (
          <CoachButton
            kind="gold"
            disabled={toggle.isPending}
            onClick={() => toggle.mutate(primary)}
          >
            Mark as done
          </CoachButton>
        )
      }
    >
      {[minutesLabel(primary.estimatedMinutes), today.primaryGoal?.title]
        .filter(Boolean)
        .join(' · ') || null}
    </Block>
  );
}

/** Actions as one-tap done toggles. */
export function ActionRows({ actions }: { actions: Action[] }) {
  const toggle = useToggleAction();
  return (
    <>
      {actions.map((a) => (
        <Row
          key={a.id}
          mark={a.status === 'done' ? <SageCheck /> : <EmptyCircle />}
          title={a.title}
          meta={actionMeta(a)}
          onClick={() => toggle.mutate(a)}
          trailing={
            <span className="sr-only">
              {a.status === 'done' ? 'Mark as not done' : 'Mark as done'}
            </span>
          }
        />
      ))}
    </>
  );
}

/** Today's actions with a way into full planning (desktop Achieve). */
export function TodayActionsList({ today }: { today: Today }) {
  const done = today.actions.filter((a) => a.status === 'done').length;
  return (
    <section>
      <Eyebrow className="border-b border-border px-5 pb-3 pt-7">
        Today's actions · {done} of {today.actions.length} done
      </Eyebrow>
      {today.actions.length === 0 && (
        <p className="border-b border-border px-5 py-4 text-sm text-muted-foreground">
          Nothing planned for today yet.
        </p>
      )}
      <ActionRows actions={today.actions} />
      <p className="px-5 py-4">
        <Link to="/achieve/actions" className="eyebrow underline underline-offset-4 hover:text-ink">
          Plan and manage actions
        </Link>
      </p>
    </section>
  );
}

export function NextMilestoneRow({ milestone }: { milestone: Milestone | null }) {
  if (!milestone) return null;
  return (
    <Row
      mark={<EmptyCircle />}
      title={milestone.title}
      meta="Next milestone"
      to={`/achieve/goals/${milestone.goalId}`}
    />
  );
}

/** Today's Success Score, opening its breakdown. */
export function ScoreLink({ today, className }: { today: Today; className?: string }) {
  return (
    <Link
      to="/achieve/score"
      className={cn(
        'block transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-verde',
        className,
      )}
    >
      <Stat
        eyebrow="Today's Success Score"
        value={today.score.total}
        label={today.score.label}
        progress={today.score.total}
        note={today.streak.current > 0 ? `${today.streak.current}-day success streak` : undefined}
      />
    </Link>
  );
}

/**
 * The four alignment axes for today (SC-27), stacked so each row reads as the
 * blueprint prints it: axis, score, band. Phone: the whole block opens the
 * guidance. Desktop: the same meters with an explicit link to Align.
 */
export function AlignmentSummary({ today, layout }: { today: Today; layout: 'phone' | 'desktop' }) {
  const a = today.alignment;
  if (!a) {
    if (today.profile.hasBirthDetails) return null;
    return (
      <Block
        eyebrow="Today's alignment"
        title="Add your birth details"
        action={
          <Link to="/settings" className={linkButton}>
            Add birth details
          </Link>
        }
      >
        Your birth chart personalizes the daily Vedic guidance.
      </Block>
    );
  }
  if (layout === 'phone') {
    return (
      <Link
        to="/align/vedic"
        className="block border-b border-border px-5 py-6 transition-colors hover:bg-surface"
      >
        <Eyebrow>Today's alignment</Eyebrow>
        <div className="mt-4 space-y-4">
          <AlignmentMeters alignment={a} />
        </div>
        <p className="mt-4 text-xs text-muted-foreground">{REFLECTIVE}</p>
      </Link>
    );
  }
  return (
    <section className="border-b border-border px-5 py-6 lg:py-8">
      <Eyebrow>Today's alignment</Eyebrow>
      <div className="mt-5 space-y-5">
        <AlignmentMeters alignment={a} />
      </div>
      <div className="mt-4 flex flex-col items-start gap-1 xl:flex-row xl:items-center xl:justify-between xl:gap-4">
        <p className="text-xs leading-relaxed text-muted-foreground">{REFLECTIVE}</p>
        <Link
          to="/align"
          className="eyebrow inline-flex min-h-11 shrink-0 items-center underline underline-offset-4 hover:text-ink"
        >
          Today's guidance
        </Link>
      </div>
    </section>
  );
}

/** Today's affirmation with the one-tap practice that moves the Mindset score. */
export function AffirmationBlock({
  today,
  eyebrow = "Today's affirmation",
  cta = 'I said it',
}: {
  today: Today;
  eyebrow?: string;
  cta?: string;
}) {
  const { affirmed, pending, affirm, share } = useAffirmation(today);
  return (
    <Block
      eyebrow={eyebrow}
      title={<span className="italic">{today.affirmation}</span>}
      action={
        <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
          {affirmed ? (
            <p className="flex items-center gap-2 text-sm text-sage">
              <SageCheck size={18} /> Repeated today
            </p>
          ) : (
            <CoachButton disabled={pending} onClick={affirm}>
              {cta}
            </CoachButton>
          )}
          <CoachButton kind="quiet" onClick={() => void share()}>
            Share
          </CoachButton>
        </div>
      }
    />
  );
}

export function CoachInsight({ insight }: { insight: string }) {
  return <Block eyebrow="Your coach says">{insight}</Block>;
}

/**
 * The tenant's community, when one is set. An outside link on purpose: the
 * groups live in Telegram, Facebook or Viber, and on a phone the WebView hands
 * a `_blank` link to the app that owns it.
 */
export function CommunityCard({
  community,
}: {
  community: { url: string; label: string | null } | null;
}) {
  if (!community) return null;
  return (
    <Block
      eyebrow="Community"
      title={community.label ?? 'Join the community'}
      action={
        <a
          href={community.url}
          target="_blank"
          rel="noopener noreferrer"
          className={linkButton}
          onClick={() => coaching.event('community_opened')}
        >
          Open
        </a>
      }
    >
      Members, updates and support, in one place.
    </Block>
  );
}

/** The guided program the member is in: where they are and the way into today's day. */
export function ProgramCard({ program }: { program: ProgramSummary | null }) {
  const e = program?.enrollment;
  if (!program || !e) return null;
  const startsLater =
    e.completedDays === 0 &&
    e.currentDay === 1 &&
    e.startedOn > new Date().toISOString().slice(0, 10);
  return (
    <Block
      eyebrow={`Your program · Day ${e.currentDay} of ${program.daysCount}`}
      title={program.title}
      action={
        <Link to={`/programs/${program.id}/days/${e.currentDay}`} className={linkButton}>
          {startsLater ? 'Read day 1' : 'Open today'}
        </Link>
      }
    >
      {e.completedDays} of {program.daysCount} days done.
    </Block>
  );
}

/** The day's two rituals: the morning ritual and the evening review, each opening its flow. */
export function RitualRows({ today, minutes }: { today: Today; minutes: number | null }) {
  const { morning, evening } = today.ritual;
  const morningMeta = morning.completed
    ? 'Done today'
    : morning.started && morning.stepsTotal > 0
      ? `${morning.stepsCompleted} of ${morning.stepsTotal} steps`
      : minutes
        ? `${minutes} min`
        : null;
  const eveningMeta = evening.completed
    ? evening.tomorrowPriority
      ? `Tomorrow: ${evening.tomorrowPriority}`
      : 'Done today'
    : "Reflect on today and set tomorrow's priority";
  return (
    <section>
      <Eyebrow className="border-b border-border px-5 pb-3 pt-7">Daily rituals</Eyebrow>
      <Row
        mark={morning.completed ? <SageCheck /> : <EmptyCircle />}
        title="Morning ritual"
        meta={morningMeta}
        to="/ritual/morning"
      />
      <Row
        mark={evening.completed ? <SageCheck /> : <EmptyCircle />}
        title="Evening review"
        meta={eveningMeta}
        to="/review/evening"
      />
    </section>
  );
}
